import { Check, Loader2, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

const TONES = {
  neutral: "bg-plena-surface2 text-plena-text2",
  green: "bg-[#E6F7F1] text-[#137A5E]",
  yellow: "bg-[#FDF4DC] text-[#8A6414]",
  blue: "bg-[#E8F1FE] text-[#1F5FBF]",
  coral: "bg-[#FDECEA] text-[#B4372E]",
};

export const Pill = ({ tone = "neutral", children, className, ...rest }) => (
  <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium", TONES[tone], className)} {...rest}>
    {children}
  </span>
);

export const statusTone = (s) =>
  ({ SUBMITTED: "green", COMPLETED: "green", ACTIVE: "green", IN_PROGRESS: "yellow", DRAFT: "neutral", NOT_STARTED: "neutral",
    PENDING: "neutral", REVOKED: "coral", CLOSED: "neutral" }[s] || "neutral");

export const StatusDot = ({ status }) => (
  <span
    aria-hidden="true"
    className={cn("inline-block h-2 w-2 rounded-full", {
      "bg-plena-success": status === "COMPLETED" || status === "SUBMITTED",
      "bg-plena-yellow": status === "IN_PROGRESS",
      "border border-plena-border bg-white": status === "PENDING" || status === "NOT_STARTED",
      "bg-plena-coral": status === "REVOKED",
    })}
  />
);

export const AutosaveIndicator = ({ state }) => {
  if (state === "idle") return null;
  return (
    <span
      data-testid="autosave-indicator"
      className={cn("inline-flex items-center gap-1.5 text-xs font-medium transition-opacity duration-200", {
        "text-plena-text2": state === "saving",
        "text-plena-success": state === "saved",
        "text-plena-coral": state === "error",
      })}
      role="status"
    >
      {state === "saving" && <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Kaydediliyor…</>}
      {state === "saved" && <><Check className="h-3.5 w-3.5" /> Kaydedildi</>}
      {state === "error" && <><AlertCircle className="h-3.5 w-3.5" /> Kaydedilemedi</>}
    </span>
  );
};

export const EmptyState = ({ title, description, action, testId }) => (
  <div data-testid={testId} className="plena-card flex flex-col items-start gap-2 px-8 py-12">
    <p className="font-heading text-base font-semibold">{title}</p>
    {description && <p className="max-w-md text-sm text-plena-text2">{description}</p>}
    {action && <div className="mt-3">{action}</div>}
  </div>
);

export const PageHeader = ({ eyebrow, title, description, actions }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div>
      {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
      <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">{title}</h1>
      {description && <p className="mt-2 max-w-2xl text-sm text-plena-text2">{description}</p>}
    </div>
    {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
  </div>
);

export const ProgressBar = ({ value, total, tone = "bg-plena-text" }) => {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-plena-surface2" role="progressbar" aria-valuenow={value} aria-valuemax={total}>
      <div className={cn("h-full rounded-full transition-[width] duration-300", tone)} style={{ width: `${pct}%` }} />
    </div>
  );
};

export const Spinner = ({ label = "Yükleniyor…" }) => (
  <div className="flex items-center gap-2 py-16 text-sm text-plena-text2" role="status">
    <Loader2 className="h-4 w-4 animate-spin" /> {label}
  </div>
);
